import React from 'react';
import { Message, Form, Button, Segment } from 'semantic-ui-react';

const CODE_LENGTH = 6;
const COUNTDOWN_SECONDS = 15 * 60;
var MFA_BOXES_CSS = '.mfa-code-boxes{width:100%}@media (min-width:480px){.mfa-code-boxes{max-width:280px;width:auto}}';

/**
 * MFA verification form: 6 single-digit boxes, paste support, optional countdown, resend.
 * Mobile: full width; desktop: constrained width.
 */
export default class MfaVerifyForm extends React.Component {

	state = {
		code: '',
		secondsLeft: COUNTDOWN_SECONDS,
		countdownActive: true
	};

	_inputRefs = [];

	componentDidMount() {
		this._timer = setInterval(this.tick, 1000);
		this.focusInput(0);
	}

	componentWillUnmount() {
		if (this._timer) clearInterval(this._timer);
	}

	componentDidUpdate(prevProps) {
		const becameVisible = this.props.visible && !prevProps.visible;
		const ticketRefreshed = this.props.visible && this.props.ticket && this.props.ticket !== prevProps.ticket;
		if (becameVisible || ticketRefreshed) {
			this.setState({ code: '', secondsLeft: COUNTDOWN_SECONDS, countdownActive: true });
			if (this._timer) clearInterval(this._timer);
			this._timer = setInterval(this.tick, 1000);
			this.focusInput(0);
		}
	}

	tick = () => {
		if (!this.state.countdownActive) return;
		this.setState((prev) => {
			const next = prev.secondsLeft - 1;
			if (next <= 0) {
				if (this._timer) clearInterval(this._timer);
				this._timer = null;
				return { secondsLeft: 0, countdownActive: false };
			}
			return { secondsLeft: next };
		});
	};

	focusInput = (index) => {
		setTimeout(() => {
			var el = this._inputRefs[index];
			if (el) el.focus();
		}, 50);
	};

	handleDigitChange = (index, e) => {
		var raw = e.target.value.replace(/\D/g, '');
		// macOS/iOS "Fill code" can inject the whole OTP into the first box
		if (raw.length > 1) {
			var full = raw.slice(0, CODE_LENGTH);
			this.setState({ code: full }, () => {
				this.focusInput(Math.min(full.length, CODE_LENGTH - 1));
			});
			return;
		}
		var val = raw.slice(-1);
		this.setState((prev) => {
			var code = prev.code.split('');
			if (code.length < index) code = code.concat(new Array(index - code.length).fill(''));
			code[index] = val;
			var newCode = code.join('').slice(0, CODE_LENGTH);
			return { code: newCode };
		}, () => {
			if (val && index < CODE_LENGTH - 1) this.focusInput(index + 1);
		});
	};

	handleDigitKeyDown = (index, e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			this.submit();
			return;
		}
		if (e.key === 'Backspace' && !this.state.code[index] && index > 0) {
			e.preventDefault();
			this.setState((prev) => ({ code: prev.code.slice(0, index - 1) + prev.code.slice(index) }), () => this.focusInput(index - 1));
			return;
		}
		if (e.key === 'ArrowLeft' && index > 0) {
			e.preventDefault();
			this.focusInput(index - 1);
			return;
		}
		if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
			e.preventDefault();
			this.focusInput(index + 1);
			return;
		}
	};

	handlePaste = (e) => {
		e.preventDefault();
		var pasted = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
		if (pasted) {
			this.setState({ code: pasted }, () => {
				this.focusInput(Math.min(pasted.length, CODE_LENGTH - 1));
			});
		}
	};

	submit = () => {
		const { code } = this.state;
		if (code.length === CODE_LENGTH) {
			this.props.onVerify(this.props.ticket, code);
		}
	};

	handleResend = () => {
		this.setState({ code: '', secondsLeft: COUNTDOWN_SECONDS, countdownActive: true });
		if (this._timer) clearInterval(this._timer);
		this._timer = setInterval(this.tick, 1000);
		this.props.onResend();
		this.focusInput(0);
	};

	render() {
		const { ticket, error, isLoading, onBack, embedded } = this.props;
		const { code, secondsLeft, countdownActive } = this.state;
		const canSubmit = code.length === CODE_LENGTH && !isLoading;
		const canResend = !isLoading && (!countdownActive || secondsLeft <= 0);
		const digits = code.split('');

		if (!ticket) return null;

		var boxStyle = {
			flex: '1 1 0',
			minWidth: 0,
			width: 0,
			fontSize: '1.5rem',
			textAlign: 'center',
			padding: '0.5rem 0.2rem',
			borderColor: '#000'
		};

		var content = (
			<React.Fragment>
				<p style={{ marginBottom: '1rem' }}>
					Add meg az alkalmazásodban megjelenő <strong>6 jegyű kódot</strong>. A kód 15 percig érvényes.
				</p>
				<Form>
					<Form.Field>
						<label>MFA kód</label>
						<div
							className="mfa-code-boxes"
							style={{
								display: 'flex',
								gap: '0.35rem',
								justifyContent: 'center',
								margin: '0 auto'
							}}
						>
							{[0, 1, 2, 3, 4, 5].map(function (i) {
								return (
									<input
										key={i}
										ref={function (el) { this._inputRefs[i] = el; }.bind(this)}
										type="text"
										inputMode="numeric"
										pattern="[0-9]*"
										autoComplete={i === 0 ? 'one-time-code' : 'off'}
										value={digits[i] || ''}
										onChange={this.handleDigitChange.bind(this, i)}
										onKeyDown={function (e) { this.handleDigitKeyDown(i, e); }.bind(this)}
										onPaste={this.handlePaste}
										maxLength={i === 0 ? CODE_LENGTH : 1}
										aria-label={'Kód ' + (i + 1) + '. számjegy'}
										style={boxStyle}
									/>
								);
							}, this)}
						</div>
						<style>{MFA_BOXES_CSS}</style>
					</Form.Field>
					{countdownActive && secondsLeft > 0 && (
						<p style={{ marginTop: '-0.5rem', marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
							Kód érvényessége: <strong>{secondsLeft}</strong> mp
						</p>
					)}
					<div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
						<Button primary onClick={this.submit} disabled={!canSubmit}>
							Ellenőrzés
						</Button>
						{onBack && (
							<Button basic onClick={onBack}>Vissza</Button>
						)}
						{canResend && (
							<Button basic onClick={this.handleResend}>
								Kód újraküldése
							</Button>
						)}
					</div>
				</Form>
			</React.Fragment>
		);

		if (embedded) {
			return <div className="mfa-verify-form">{content}</div>;
		}

		return (
			<div className="mfa-verify-form">
				{error && <Message error>{error}</Message>}
				<Segment padded="very" loading={isLoading}>
					{content}
				</Segment>
			</div>
		);
	}
}
